import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchDraftCarePlanData,
  deleteCarePlan,
  publishCarePlan,
  lockCarePlan,
  unlockCarePlan,
  fetchCarePlanInstance,
  fetchSectionsData,
  fetchItemsData,
} from "../../../redux/actions/careplanActions";
import {
  checkLockStatus,
  logDraftCarePlanActionService,
} from "../../../services/dashboardServices";
// import { fetchActiveUsers } from "../../../redux/actions/userActions";
import { formatDate } from "../../../utils/dateUtils";
// import AddMdtUser from "./AddMdtUser";
import Menu from "../../common/Menu";
import ViewCarePlan from "./ViewCarePlan";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import EditCarePlan from "./EditCarePlan";
import CarePlanMDTUsers from "./CarePlanMDTUsers";
import DraftPlanView from "./DraftPlanView";
import CarePlanAuditLogs from "../AuditLog/CarePlanAuditLogs";
// import MDTUsers from "../../dashboard/MDTUser/MDTUsers";
import TextField from "../../common/TextField";
import Pagination from "../../common/Pagination";
import { useDebounce } from "../../../hooks/useDebounce";
import {
  setDraftSearchQuery,
  setMode,
} from "../../../redux/slices/careplanSlice";
import Avatar from "../../common/Avatar";
import {
  AddUserIcon,
  DeleteIcon,
  EyeIcon,
  EditIcon,
  ExportIcon,
  PublishIcon,
  SearchIcon,
  SortArrowIcon,
  InfoFilled,
  LogIcon,
  UnlockIcon,
} from "../../common/Icons";
import "./CarePlans.css";

const DraftCarePlans = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Selectors for draft care plans
  const draftCarePlans = useSelector((state) => state.careplan.draftCarePlans);
  const totalRows = useSelector((state) => state.careplan.draftTotalRows);
  const draftLoading = useSelector((state) => state.careplan.draftLoading);
  const draftError = useSelector((state) => state.careplan.draftError);
  const currentPage = useSelector((state) => state.careplan.draftCurrentPage);
  const rowsPerPage = useSelector((state) => state.careplan.draftRowsPerPage);
  const draftSearchQuery = useSelector(
    (state) => state.careplan.draftSearchQuery
  );
  const { sectionsLoaded, itemsLoaded } = useSelector(
    (state) => state.careplan
  );
  const userProfile = useSelector((state) => state.auth.userProfile);
  const isAdmin = useSelector((state) => state.auth.isAdmin);

  // Local component states
  const [selectedCarePlan, setSelectedCarePlan] = useState(null); // Current selected care plan
  // const [activeUsers, setActiveUsers] = useState([]); // List of active users
  const [addMdtUser, setAddMdtUser] = useState(false); // Control popup visibility
  const [viewCarePlan, setViewCarePlan] = useState(false);
  const [editCarePlan, setEditCarePlan] = useState(false);
  const [showDraftPlan, setShowDraftPlan] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [showPublishConfirmModal, setShowPublishConfirmModal] = useState(false);
  const [showCarePlanLockedModal, setShowCarePlanLockedModal] = useState(false);
  const [serviceUserName, setServiceUserName] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [planLockedBy, setPlanLockedBy] = useState(null); // Track who locked the care plan
  const [showPublishValidationError, setShowPublishValidationError] =
    useState(false);
  const [isImportantInfoEmpty, setIsImportantInfoEmpty] = useState(false);
  const [showUnlockConfirmModal, setShowUnlockConfirmModal] = useState(false); // Track if the unlock modal should be shown
  const [showAuditLog, setShowAuditLog] = useState(false);
  // Sorting state: field and order (asc/desc)
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [isLoading, setIsLoading] = useState(false);

  // Debounce the search input
  const debouncedSearchInput = useDebounce(searchInput, 500);

  // Update filters when the debounced search input changes
  useEffect(() => {
    const newFilters = debouncedSearchInput
      ? { nhsNumber: debouncedSearchInput, createdBy: debouncedSearchInput }
      : { nhsNumber: "", createdBy: "" };

    dispatch(setDraftSearchQuery(newFilters));
  }, [debouncedSearchInput, dispatch]);

  useEffect(() => {
    // Build ordering parameter: if sortField is set, prefix with "-" if descending.
    const ordering = sortField
      ? sortOrder === "asc"
        ? sortField
        : "-" + sortField
      : "";
    dispatch(
      fetchDraftCarePlanData({
        page: currentPage,
        rowsPerPage,
        searchQuery: draftSearchQuery,
        ordering,
      })
    );
  }, [
    dispatch,
    currentPage,
    rowsPerPage,
    draftSearchQuery,
    addMdtUser,
    selectedCarePlan,
    sortField,
    sortOrder,
  ]);

  const handleEditProcess = async (carePlan) => {
    // Guard against double-clicks
    if (isLoading) return;

    try {
      setIsLoading(true);
      dispatch(setMode("edit")); // ← important so leave the answered items to avoid override by empty
      // STEP 1: Check lock status
      const statusResponse = await checkLockStatus(carePlan.id);
      if (statusResponse.is_locked) {
        // If locked by someone else, show an appropriate modal
        if (statusResponse.locked_by?.id !== userProfile.id) {
          setPlanLockedBy(statusResponse?.locked_by?.name);
          setShowCarePlanLockedModal(true);
          return;
        } else {
          // If already locked by the user, you might extend the lock
          await dispatch(lockCarePlan(carePlan.id));
        }
      } else {
        // Lock the care plan if not locked
        await dispatch(lockCarePlan(carePlan.id));
      }

      // STEP 2: Preload care plan details (instance, sections, items)
      await dispatch(fetchCarePlanInstance(carePlan.id));
      if (!sectionsLoaded) {
        await dispatch(fetchSectionsData());
      }
      if (!itemsLoaded) {
        await dispatch(fetchItemsData());
      }

      // STEP 3: Everything is loaded, now open the Edit modal
      setSelectedCarePlan(carePlan);
      setEditCarePlan(true);
    } catch (error) {
      console.error("Error during pre-edit process:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    dispatch(
      fetchDraftCarePlanData({
        page: newPage,
        rowsPerPage,
        searchQuery: draftSearchQuery,
        ordering: sortField
          ? sortOrder === "asc"
            ? sortField
            : "-" + sortField
          : "",
      })
    );
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    dispatch(
      fetchDraftCarePlanData({
        page: 1, // reset to first page
        rowsPerPage: newRowsPerPage,
        searchQuery: draftSearchQuery,
        ordering: sortField
          ? sortOrder === "asc"
            ? sortField
            : "-" + sortField
          : "",
      })
    );
  };

  const data = draftCarePlans[currentPage] || [];
  // Calculate total pages
  const totalPages = Math.ceil(totalRows / rowsPerPage);

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  // Handle sort click on a header column
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle order if same field clicked
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      // Otherwise set new sort field with default ascending order
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleConfirmDelete = () => {
    const form = document.getElementById("deleteConfirmationForm"); // Select the form
    const deleteTextField = document.querySelector("input[name=deleteText]");
    // Check if the field is empty
    if (!deleteTextField.value.trim()) {
      deleteTextField.setCustomValidity("This field is required."); // Show empty field message
    } else if (deleteTextField.value !== "Delete") {
      deleteTextField.setCustomValidity(
        'Please type "Delete" to confirm deletion.'
      ); // Show custom message
    } else {
      deleteTextField.setCustomValidity(""); // Clear the custom validation message
    }

    // Trigger form validation
    if (form && !form.reportValidity()) {
      return; // Stop if the form is invalid
    }

    setDeleteText(""); //reset the delete text otherwise the next time the text box show Delete
    setShowDeleteConfirmModal(false);
    dispatch(
      deleteCarePlan({ carePlanId: selectedCarePlan.id, type: "draft" })
    );
  };

  const handlePublishCarePlan = () => {
    setServiceUserName("");
    dispatch(
      publishCarePlan({ carePlanId: selectedCarePlan.id, serviceUserName })
    )
      .then(() => {
        setShowPublishConfirmModal(false);
        navigate("/admin_dashboard/active-care-plans"); // After publishing redirect to active care plans
      })
      .catch((error) => {
        setShowPublishConfirmModal(false);
      });
  };

  const handleEditCarePlan = async (carePlan) => {
    try {
      // First check current lock status
      const statusResponse = await checkLockStatus(carePlan.id);

      if (statusResponse.is_locked) {
        // Update Redux store with lock status
        dispatch({
          type: "careplan/updateCarePlanLockStatus",
          payload: {
            id: carePlan.id,
            is_locked: true,
            locked_by: statusResponse.locked_by,
          },
        });

        // Update local state with locked_by info so the modal can display it
        setSelectedCarePlan({
          ...carePlan,
          is_locked: true,
          locked_by: statusResponse.locked_by,
        });

        if (statusResponse.locked_by?.id === userProfile.id) {
          // The current user already holds the lock; proceed to extend it.
          const response = await dispatch(lockCarePlan(carePlan.id));

          if (response.meta.requestStatus === "fulfilled") {
            // setSelectedCarePlan(carePlan);
            setEditCarePlan(true);
          }
        } else {
          setShowCarePlanLockedModal(true);
        }
        return;
      }

      // Not locked - attempt to lock
      const response = await dispatch(lockCarePlan(carePlan.id));
      if (response.meta.requestStatus === "fulfilled") {
        setSelectedCarePlan(carePlan);
        setEditCarePlan(true);
      } else if (response.meta.requestStatus === "rejected") {
        if (response.payload?.code === "ALREADY_LOCKED") {
          setShowCarePlanLockedModal(true);
        }
      }
    } catch (error) {
      // This will only catch non-409 errors
      console.error("Error locking care plan:", error);
      if (error.code === "ALREADY_LOCKED") {
        setShowCarePlanLockedModal(true);
      }
    }
  };

  const handlePublishClick = async (plan) => {
    try {
      const status = await checkLockStatus(plan.id);
      if (status.is_locked && status.locked_by.id !== userProfile.id) {
        setPlanLockedBy(status.locked_by.name);
        setShowCarePlanLockedModal(true);
        return;
      }
      // not locked (or locked by me), so fire the existing “validateBeforePublish”:
      await validateBeforePublish(plan);
    } catch (err) {
      console.error("Lock check failed", err);
    }
  };

  const handleDeleteClick = async (plan) => {
    try {
      const status = await checkLockStatus(plan.id);
      if (status.is_locked && status.locked_by.id !== userProfile.id) {
        setPlanLockedBy(status.locked_by.name);
        setShowCarePlanLockedModal(true);
        return;
      }
      // not locked (or locked by me), so show the delete‐confirmation popup:
      setSelectedCarePlan(plan);
      setShowDeleteConfirmModal(true);
    } catch (err) {
      console.error("Lock check failed", err);
    }
  };

  const validateBeforePublish = async (carePlanRow) => {
    // load the full draft care plan with sections & answers
    const response = await dispatch(fetchCarePlanInstance(carePlanRow.id));
    const instance = response.payload;

    // find any section (except NHS‑number) with at least one empty answer
    const hasEmpty = instance.sections.some((section) => {
      if (section.id === 1) return false; // skip the NHS number section
      return section.items.some(
        (item) => !item.answer || item.answer.trim() === ""
      );
    });

    if (hasEmpty) {
      setShowPublishValidationError(true);
    } else {
      if (instance.important_info !== "") {
        setSelectedCarePlan(carePlanRow);
        setShowPublishConfirmModal(true);
        setIsImportantInfoEmpty(false);
      } else {
        // If important info is empty, show a message or handle accordingly
        setIsImportantInfoEmpty(true);
        setShowPublishValidationError(true);
      }
    }
  };

  const handleMenuClick = (carePlan, action) => {
    setSelectedCarePlan(carePlan); // Set the selected care plan
    if (action === "view") {
      logDraftCarePlanActionService(carePlan.id, "view"); // Log the draft care plan view service
      setViewCarePlan(true);
    }
    if (action === "edit") handleEditCarePlan(carePlan);
    if (action === "addUser") setAddMdtUser(true);
    if (action === "delete") setShowDeleteConfirmModal(true);
    if (action === "publish") validateBeforePublish(carePlan);
    if (action === "draftView") {
      setShowDraftPlan(true);
      logDraftCarePlanActionService(carePlan.id, "print"); // Log the draft care plan print service
    }
    if (action === "auditLog") setShowAuditLog(true);
  };

  // Popup Menu Items
  const getMenuItems = (row) => {
    const isLocked = row.locked_by !== null;
    const isCurrentUserLocked = row.locked_by?.id === userProfile.id;
    // show the tooltip for the other users who is not locked the care plan as it is locked
    // if the current user is admin, show the unlock option
    if (isLocked && !isCurrentUserLocked) {
      const items = [
        {
          label: `Care plan locked by ${row.locked_by.name}`,
          icon: <InfoFilled />,
          action: () => {},
          disabled: true,
        },
      ];
      if (isAdmin) {
        items.push({
          label: "Unlock care plan",
          icon: <UnlockIcon color="#0065C0" />,
          action: () => {
            setSelectedCarePlan(row);
            setShowUnlockConfirmModal(true);
          },
          color: "#0065C0",
        });
      }
      return items;
    }

    return [
      {
        label: "View Draft Plan",
        icon: <EyeIcon />,
        action: () => handleMenuClick(row, "view"),
      },
      {
        label: "Edit",
        icon: <EditIcon />,
        action: () => handleEditProcess(row),
        // action: () => setEditCarePlanNHS(row.nhsNumber),
      },
      {
        label: "Plan Access",
        icon: <AddUserIcon />,
        action: () => handleMenuClick(row, "addUser"),
        // action: () => setAddMdtUser(true),
      },
      {
        label: "Publish Plan",
        icon: <PublishIcon />,
        action: () => handlePublishClick(row),
      },
      {
        label: "Print View",
        icon: <ExportIcon />,
        action: () => handleMenuClick(row, "draftView"),
      },
      {
        label: "Audit Log",
        icon: <LogIcon />,
        action: () => handleMenuClick(row, "auditLog"),
      },
      {
        label: "Delete Plan",
        icon: <DeleteIcon />,
        action: () => handleDeleteClick(row),
      },
    ];
  };

  if (draftLoading) {
    return <div>Loading...</div>;
  }

  if (draftError) {
    return <div>Error: There is an error please check again</div>;
  }

  return (
    <>
      <div className="draftplan-container">
        <div className="search-filter-bar">
          <div className="search-bar">
            <div className="search-icon">
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search by NHS number or Created by"
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <div className="table-wrapper">
          <table className="care-plan-table">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("nhs_number")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="header-content">
                    NHS number
                    <span className="header-icon">
                      {sortField === "nhs_number" ? (
                        sortOrder === "asc" ? (
                          <SortArrowIcon direction="asc" />
                        ) : (
                          <SortArrowIcon direction="desc" />
                        )
                      ) : (
                        <SortArrowIcon />
                      )}
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("created_by__profile__name")}
                  style={{ cursor: "pointer" }}
                  className="hide-on-mobile"
                >
                  <div className="header-content">
                    Created by
                    <span className="header-icon">
                      {sortField === "created_by__profile__name" ? (
                        sortOrder === "asc" ? (
                          <SortArrowIcon direction="asc" />
                        ) : (
                          <SortArrowIcon direction="desc" />
                        )
                      ) : (
                        <SortArrowIcon />
                      )}
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("updated_at")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="header-content">
                    Last updated
                    <span className="header-icon">
                      {sortField === "updated_at" ? (
                        sortOrder === "asc" ? (
                          <SortArrowIcon direction="asc" />
                        ) : (
                          <SortArrowIcon direction="desc" />
                        )
                      ) : (
                        <SortArrowIcon />
                      )}
                    </span>
                  </div>
                </th>
                <th>
                  <div className="header-content">Users</div>
                </th>

                <th className="hide-on-mobile"></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr key={index} className={row.locked_by ? "locked-row" : ""}>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedCarePlan(row);
                        logDraftCarePlanActionService(row.id, "view");
                        setViewCarePlan(true);
                      }}
                    >
                      {row.nhsNumber}
                    </button>
                  </td>
                  <td className="hide-on-mobile">{row.created_by}</td>
                  <td>{formatDate(row.updated_at)}</td>
                  <td>
                    <div className="mdt-users">
                      {row.assigned_users && row.assigned_users.length > 0 ? (
                        row.assigned_users.map((user, idx) => (
                          <div
                            key={idx}
                            style={{
                              zIndex: row.assigned_users.length - idx,
                              marginLeft: idx === 0 ? 0 : -12,
                              position: "relative",
                            }}
                          >
                            <Avatar
                              key={idx}
                              profilePictureUrl={user.profile_picture}
                              name={user.name}
                              useDefault={true}
                              size={28}
                              bgColor="lightgray"
                            />
                          </div>
                        ))
                      ) : (
                        <span></span>
                      )}
                    </div>
                  </td>
                  <td className="hide-on-mobile">
                    {row.locked_by && (
                      <div className="draftplan-locked">
                        Locked{" "}
                        <span className="draftplan-locked-icon">
                          <InfoFilled />
                        </span>
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="draftplan-menu-icon">
                      <Menu menuItems={getMenuItems(row)} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination Component */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          totalRows={totalRows}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>
      {viewCarePlan && selectedCarePlan && (
        <ViewCarePlan
          carePlanId={selectedCarePlan.id}
          version={selectedCarePlan.version}
          onClose={() => {
            setViewCarePlan(false);
            setSelectedCarePlan(null);
          }}
        />
      )}
      {editCarePlan && selectedCarePlan && (
        <EditCarePlan
          key={selectedCarePlan.id}
          carePlanId={selectedCarePlan.id}
          nhsNumber={selectedCarePlan.nhsNumber}
          onSave={() => {
            setEditCarePlan(false);
            setSelectedCarePlan(null);
          }}
          onClose={() => {
            setEditCarePlan(false);
            setSelectedCarePlan(null);
          }}
        />
      )}
      {/* Add MDT User Popup */}
      {addMdtUser && selectedCarePlan && (
        <FullScreenOverlay
          title="Manage Plan Access"
          popupType="add-user"
          buttons={[]}
          iconClose={true}
          onClose={() => setAddMdtUser(false)}
        >
          <CarePlanMDTUsers selectedCarePlan={selectedCarePlan} />
        </FullScreenOverlay>
      )}
      {/* Draft Plan View */}
      {showDraftPlan && selectedCarePlan && (
        <DraftPlanView
          carePlanId={selectedCarePlan.id}
          onClose={() => {
            setShowDraftPlan(false);
            setSelectedCarePlan(null);
          }}
        />
      )}
      {/* Confirm Delete Care Plan Popup */}
      {showDeleteConfirmModal && (
        <FullScreenOverlay
          width="480px"
          height="300px"
          isPopup={true}
          popupType="delete"
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => {
                setShowDeleteConfirmModal(false);
                setDeleteText("");
                // setItemToDelete(null);
              },
            },
            {
              label: "Yes, Delete",
              onClick: handleConfirmDelete, // Handle delete confirmation
              variant: "danger",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">Confirm Care Plan deletion</h5>
          <p>
            Are you sure you want to delete the
            <span className="confirm-delete-nhs">
              {" "}
              Care Plan {selectedCarePlan.nhsNumber}
            </span>
            ? This action cannot be undone and all the data will be permanently
            removed.
          </p>
          <p>
            Type <b>"Delete"</b> to confirm
          </p>
          <form
            id="deleteConfirmationForm"
            onSubmit={(e) => e.preventDefault()}
          >
            <TextField
              placeholder=""
              maxLength={10}
              showCharacterLimit={false}
              name="deleteText"
              value={deleteText}
              onChange={(e) => {
                setDeleteText(e.target.value); // Update state
                e.target.setCustomValidity(""); // Clear custom validation message
              }}
              autoComplete="off" // otherwise the Delete will show in autocomplete which is not the good behaviour here
              required
            />
          </form>
        </FullScreenOverlay>
      )}
      {showPublishConfirmModal && (
        <FullScreenOverlay
          width="524px"
          height="225px"
          isSlimModal={true}
          isPopup={true}
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => {
                setShowPublishConfirmModal(false);
              },
            },
            {
              label:
                selectedCarePlan.version > 1 ? "Yes, Replace" : "Yes, Publish",
              onClick: handlePublishCarePlan, // Handle delete confirmation
              variant: "primary",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">Publish careplan?</h5>
          {selectedCarePlan.version > 1 ? (
            <p>Are you sure you want to replace plan?</p>
          ) : (
            <p>Are you sure you want to make this draft care plan active?</p>
          )}
        </FullScreenOverlay>
      )}
      {
        // Show a modal if the care plan is locked
        showCarePlanLockedModal && (
          <FullScreenOverlay
            width="524px"
            height="225px"
            isSlimModal={true}
            isPopup={true}
            buttons={[
              {
                label: "OK",
                onClick: () => {
                  setShowCarePlanLockedModal(false);
                },
              },
            ]}
          >
            <h5 className="semi-bold">Care Plan is locked</h5>
            <p>
              The care plan is currently being edited by <b>{planLockedBy}</b>.
              Please try again later.
            </p>
          </FullScreenOverlay>
        )
      }
      {showPublishValidationError && (
        <FullScreenOverlay
          width="524px"
          height="225px"
          isSlimModal={true}
          isPopup={true}
          buttons={[
            {
              label: "Close",
              onClick: () => {
                setShowPublishValidationError(false);
                setIsImportantInfoEmpty(false);
              },
            },
          ]}
        >
          <h5 className="semi-bold">This care plan has empty items.</h5>
          {isImportantInfoEmpty ? (
            <p>
              Please fill "Important information if I need to go into hospital"
              to publish this care plan.
            </p>
          ) : (
            <p>Please complete to be able to publish this care plan</p>
          )}
        </FullScreenOverlay>
      )}
      {showUnlockConfirmModal && (
        <FullScreenOverlay
          width="524px"
          height="225px"
          isSlimModal={true}
          isPopup={true}
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => {
                setShowUnlockConfirmModal(false);
              },
            },
            {
              label: "Yes, Unlock",
              onClick: async () => {
                await dispatch(unlockCarePlan(selectedCarePlan.id));
                setShowUnlockConfirmModal(false);
                dispatch(
                  fetchDraftCarePlanData({
                    page: currentPage,
                    rowsPerPage,
                    searchQuery: draftSearchQuery,
                    ordering: sortField
                      ? sortOrder === "asc"
                        ? sortField
                        : `-${sortField}`
                      : "",
                    forceRefresh: true,
                  })
                );
              },
              variant: "primary",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">Unlock care plan?</h5>
          <p>
            Are you sure you want to unlock this care plan?
            <br />
            If someone is editing, their changes will be lost.
          </p>
        </FullScreenOverlay>
      )}

      {showAuditLog && selectedCarePlan && (
        <CarePlanAuditLogs
          carePlanId={selectedCarePlan.id}
          isDraft={true}
          nhsNumber={selectedCarePlan.nhsNumber}
          onClose={() => setShowAuditLog(false)}
        />
      )}
    </>
  );
};

export default DraftCarePlans;
